import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form5498SAInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 5498-SA instructions for Tax Year 2025"
          description="Learn the step-by-step instructions for IRS Form 5498-SA for Tax Year 2025. TaxZerone helps you accurately report HSA and MSA contributions and rollovers."
          keywords=""
          canonical="resources/information-returns/form-5498-sa-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form{' '}
          <span className="highlight-container">
            <span className="highlight"> 5498-SA</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File Form 5498-SA easily by following the given instructions
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498SAinstruction`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-5498-sa-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 5498-SA at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Form 5498-SA
                        </strong>
                        {' reports contributions made to a '}
                        <strong style={{ fontWeight: 600 }}>
                          Health Savings Account (HSA)
                        </strong>
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          Archer Medical Savings Account (Archer MSA)
                        </strong>
                        {', or '}
                        <strong style={{ fontWeight: 600 }}>
                          Medicare Advantage Medical Savings Account (MA MSA)
                        </strong>
                        . Trustees or custodians use this form to report annual
                        contributions, including those made for the previous tax
                        year.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        {'Recipient copies must be furnished by '}
                        <strong style={{ fontWeight: 600 }}>May 31</strong>
                        {
                          ' following the calendar year being reported. The IRS filing deadline is '
                        }
                        <strong style={{ fontWeight: 600 }}>June 1</strong>
                        {' for paper filings and '}
                        <strong style={{ fontWeight: 600 }}>June 1</strong>
                        {' for electronic filings.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        {'This article covers '}
                        <strong style={{ fontWeight: 600 }}>
                          line-by-line instructions
                        </strong>
                        {
                          ', including trustee or custodian information and participant details, '
                        }
                        <strong style={{ fontWeight: 600 }}>{'Box 1 '}</strong>
                        (HSA Contributions)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 2 '}</strong>
                        (Total Contributions Made in the Current Year)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 3 '}</strong>
                        (Total HSA or Archer MSA Contributions Made During the
                        Following Year for the Current Tax Year)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 4</strong>
                        {' (Rollover Contributions)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 5 '}</strong>
                        (Fair Market Value of the Account)
                        <strong style={{ fontWeight: 600 }}>{', '}</strong>
                        {'and'}{' '}
                        <strong style={{ fontWeight: 600 }}>Box 6 (</strong>
                        Checkbox to Indicate the Account Type—HSA, Archer MSA,
                        or MA MSA) .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {' Late, incorrect, or incomplete filing of '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 5498-SA
                        </strong>
                        {' may result in IRS penalties ranging from '}
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on the delay, correction requirements, and
                        whether the filer meets electronic filing obligations.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  {' '}
                  Form 5498-SA is an IRS form used to report contributions to
                  Health Savings Accounts (HSAs), Archer Medical Savings
                  Accounts (Archer MSAs), and Medicare Advantage MSAs (MA MSAs).
                  This resource page provides an overview, filing requirements,
                  deadlines, and step-by-step instructions for completing and
                  submitting Form 5498-SA.
                </Typography>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 5498-SA?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form 5498-SA?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is the information required to file form 5498-SA?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#import" sx={{ color: '#0202ff' }}>
                          Important dates for filing Form 5498-SA
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#inst" sx={{ color: '#0202ff' }}>
                          Instructions to file Form 5498-SA
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#simple"
                          id="what"
                          sx={{ color: '#0202ff' }}
                        >
                          Simplify your filing 5498-SA form with TaxZerone
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 5498-SA?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 5498-SA is used by trustees or custodians for{' '}
                  <Link
                    href="/information-returns/efile-irs-form-5498-sa/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Health Savings Account reporting
                  </Link>
                  , including contributions (such as rollovers and qualified
                  funding distributions) to HSAs, Archer MSAs, and Medicare
                  Advantage MSAs for each account holder during the tax year. It
                  also reports the fair market value (FMV) of the account as of
                  December 31 of the tax year.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Simplify your 5498-SA process for HSA, Archer, and
                        Medicare MSA account details.
                      </Typography>
                      <Typography variant="body2">
                        E-file Form 5498-SA with TaxZerone for fast, accurate
                        reporting today.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498SAinstructions`}
                        id="who"
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who must file Form 5498-SA?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The trustee or custodian of Health Savings Accounts (HSAs),
                  Archer MSAs, or Medicare Advantage MSAs must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-5498-sa/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file form 5498-sa
                  </Link>{' '}
                  with the IRS and issue a copy to the account holder. A
                  separate form needs to be filed for each type of account plan.
                  This Form has two copies; <br id="whatis" />
                  <span style={{ fontWeight: 600 }}>Copy A</span> must be
                  transmitted to the IRS and{' '}
                  <span style={{ fontWeight: 600 }}> Copy B</span> should be
                  delivered to the participants.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is the information required to file form 5498-SA?
                </Typography>
                <Typography variant="body1">
                  These are the information that required to file IRS form
                  5498-SA
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Trustee/custodian basic information and TIN </li>
                  <li>
                    Participant’s basic information, TIN and Account number
                  </li>
                  <li>
                    Contributions made by employee/self-employed individual on
                    Archer MSA
                  </li>
                  <li>
                    Total HSA or Archer MSA contributions made in 2026 for the
                    tax year 2025{' '}
                  </li>
                  <li>Rollover contributions</li>
                  <li id="import">
                    Fair market value of the account as of December 31
                  </li>
                  <li>Account type (HSA, Archer MSA, or MA MSA) </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important dates for filing Form 5498-SA
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Make sure you don’t miss important HSA and MSA reporting
                  dates—check the{' '}
                  <Link
                    href="/resources/information-returns/form-5498-sa-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 5498-SA due date
                  </Link>{' '}
                  to stay compliant.{' '}
                </Typography>

                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Form 5498-SA
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Deadlines
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Send FMV statement </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            February 02, 2026{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Send participant copy </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            June 01, 2026{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>IRS E-filing </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            June 01, 2026{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Paper filing </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            June 01, 2026{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#bbdefb',
                    border: '1px solid #ccc',
                  }}
                  // className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          fontSize: { md: 18, sm: 16, xs: 14 },
                        }}
                      >
                        Need More Time to File Form 5498-SA?
                      </Typography>
                      <Typography variant="body1">
                        No worries! Get a{' '}
                        <span style={{ fontWeight: 600 }}>
                          30-day extension
                        </span>{' '}
                        with Form 8809 to avoid penalties and file
                        stress-free.{' '}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 180, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#213e99',
                          color: '#fff',
                          '&:hover, &:focus, &:active': { color: '#fff' },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498SAinstructions`}
                        id="inst"
                      >
                        E-file Form 8809
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Instructions to file Form 5498-SA
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Follow these step-by-step instructions to file your form
                  accurately with IRS.
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f5498-sa-trustee-img.webp" />
                  <source srcSet="/assets/images/f5498-sa-trustee-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f5498-sa-trustee-img.png"
                    loading="lazy"
                    alt="Instructions to file Form 5498-SA"
                    width={410}
                    height={122}
                    className="img-responsive"
                    sx={{
                      // border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the trustee’s or custodian’s full legal name, complete
                  address including City, State, country, ZIP code and telephone
                  number.
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f5498-sa-legal-img.webp" />
                  <source srcSet="/assets/images/f5498-sa-legal-img.png" />
                  <img
                    src="/assets/images/f5498-sa-legal-img.png"
                    loading="lazy"
                    alt=" Enter the trustee’s or custodian’s"
                    width={410}
                    height={42}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the trustee’s and participant’s TIN (Taxpayer
                  Identification Number) in the appropriate field.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-sa-participant-img.webp" />
                  <source srcSet="/assets/images/f5498-sa-participant-img.png" />
                  <img
                    src="/assets/images/f5498-sa-participant-img.png"
                    loading="lazy"
                    alt="Enter the Participant's legal name"
                    width={410}
                    height={182}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter the Participant&apos;s legal name, address including
                  apartment number, city, state, country, ZIP code and account
                  number.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-sa-contribution-img.webp" />
                  <source srcSet="/assets/images/f5498-sa-contribution-img.png" />
                  <img
                    src="/assets/images/f5498-sa-contribution-img.png"
                    loading="lazy"
                    alt="Enter the total contributions made"
                    width={296}
                    height={142}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 1
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total contributions made by the employee or
                  self-employed individual to the Archer Medical Savings Account
                  in 2025 and through April 15, 2026, for the tax year 2025.
                  Also report the gross contributions, including any excess
                  contributions, even if they were withdrawn.
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f5498-sa-total-img.webp" />
                  <source srcSet="/assets/images/f5498-sa-total-img.png" />
                  <img
                    src="/assets/images/f5498-sa-total-img.png"
                    loading="lazy"
                    alt="Enter the total contributions made to HSA"
                    width={279}
                    height={72}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total contributions made to HSA or Archer MSA in
                  2025, and include any contributions made in 2025 for the year
                  2024. Also include any qualified HSA funding distributions
                  received by participant’s during 2025. These are
                  trustee-to-trustee transfers from an IRA to an HSA, under
                  section 408(d)(9). No need to report Medicare Advantage MSA
                  contribution for the year.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-sa-archer-img.webp" />
                  <source srcSet="/assets/images/f5498-sa-archer-img.png" />
                  <img
                    src="/assets/images/f5498-sa-archer-img.png"
                    loading="lazy"
                    alt=" Enter the total contributions made"
                    width={592}
                    height={72}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total contributions made to Health Savings Account
                  or Archer Medical Savings Account in 2026 for the year 2025.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-sa-rollover-img.webp" />
                  <source srcSet="/assets/images/f5498-sa-rollover-img.png" />
                  <img
                    src="/assets/images/f5498-sa-rollover-img.png"
                    loading="lazy"
                    alt="Enter any rollover contribution"
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any rollover contribution from one Archer MSA to another
                  Archer MSA or from an Archer MSA or a Health Savings Account
                  (HSA) to another HSA.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-fair-market-img.webp" />
                  <source srcSet="/assets/images/f5498-fair-market-img.png" />
                  <img
                    src="/assets/images/f5498-fair-market-img.png"
                    loading="lazy"
                    alt=" Enter the Fair Market value"
                    width={170}
                    height={62}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Fair Market value of the participant’s account such
                  as Health Savings Account (HSA), Archer Medical Savings
                  Account (Archer MSA) or Medicare Advantage MSA (MA MSA) as of
                  December 31, 2025
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5498-sa-health-img.webp" />
                  <source srcSet="/assets/images/f5498-sa-health-img.png" />
                  <img
                    src="/assets/images/f5498-sa-health-img.png"
                    loading="lazy"
                    alt="Check the participant account"
                    width={170}
                    height={82}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }} id="simple">
                  Check the participant account type such as Health Savings
                  Account (HSA), Archer Medical Savings Account (Archer MSA) or
                  Medicare Advantage MSA (MA MSA) that is being reported on this
                  form.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Simplify your filing 5498-SA form with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  With TaxZerone you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-5498-sa/"
                    sx={{ color: '#0202ff' }}
                  >
                    file IRS form 5498-SA
                  </Link>{' '}
                  with easy 3 steps:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  <span style={{ fontWeight: 600 }}> Step 1:</span> Log in to
                  your{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498SAinstructions`}
                    sx={{ color: '#0202ff' }}
                  >
                    TaxZerone Free Account
                  </Link>{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  <span style={{ fontWeight: 600 }}> Step 2:</span> Choose tax
                  form “5498-SA” and enter the details of Trustee, participant
                  and HSA, MSA, rollover contribution.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  <span style={{ fontWeight: 600 }}> Step 3:</span> Review and
                  transmit it to the IRS and send participant copies through
                  ZeroneVault or postal mailing.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        IRS website. For more information, check the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1099sa.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS instructions for form 5498-SA
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          General Instructions for Information Returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Experience fast and affordable tax filing with TaxZerone
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              Filing starts at{' '}
              <span style={{ fontWeight: 600 }}>$ 2.49 per</span> form and can
              go as low as{' '}
              <span style={{ fontWeight: 600 }}>$0.59 Per form</span>
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5498SAinstructions`}
              >
                E-file Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form5498SAInstructions;
